import axios from "./axios";
import { AdminApiError } from "./errors";

export interface CreatorPayoutPreview {
  creatorId: string;
  creatorName: string;
  creatorEmail?: string;
  periodStartUtc: string;
  periodEndUtc: string;
  grossTokenAmount: number;
  sharePercent: number;
  shareTokenAmount: number;
  currencyAmount: number;
  currencyCode: string;
}

export interface CreatorPayoutRecord extends CreatorPayoutPreview {
  id: string;
  status: string;
  createdAtUtc: string;
  approvedByUserId?: string | null;
  approvedAtUtc?: string | null;
  paidAtUtc?: string | null;
  paymentReference?: string | null;
  paymentNote?: string | null;
  notes?: string | null;
}

export interface CreateCreatorPayoutResult {
  record: CreatorPayoutRecord | null;
  errorMessage?: string;
  status?: number;
}

export async function getOutstandingCreatorPayouts(from?: string, to?: string): Promise<CreatorPayoutPreview[]> {
  const params: Record<string, string> = {};
  if (from) params.from = from;
  if (to) params.to = to;
  const res = await axios.get("/admin/creators/payouts/outstanding", { params });
  return Array.isArray(res.data) ? (res.data as CreatorPayoutPreview[]) : [];
}

export async function getCreatorPayouts(status?: string): Promise<CreatorPayoutRecord[]> {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  const res = await axios.get("/admin/creators/payouts", { params });
  return Array.isArray(res.data) ? (res.data as CreatorPayoutRecord[]) : [];
}

export async function createCreatorPayout(payload: {
  creatorId: string;
  from?: string;
  to?: string;
  note?: string;
}): Promise<CreateCreatorPayoutResult> {
  try {
    const res = await axios.post("/admin/creators/payouts", payload);
    return { record: res.data as CreatorPayoutRecord };
  } catch (error) {
    console.error("[admin] create payout failed", error);
    if (error instanceof AdminApiError) {
      return {
        record: null,
        errorMessage: error.message,
        status: error.status,
      };
    }

    return {
      record: null,
      errorMessage: "Payout draft olusturulamadi.",
    };
  }
}

export async function markCreatorPayoutPaid(
  payoutId: string,
  reason: string,
  options?: { paymentReference?: string; paymentNote?: string },
): Promise<boolean> {
  const normalizedReason = reason.trim();
  if (!normalizedReason) {
    return false;
  }

  try {
    await axios.post(`/admin/finance/payouts/${payoutId}/mark-paid`, {
      reason: normalizedReason,
      paymentReference: options?.paymentReference?.trim() || undefined,
      paymentNote: options?.paymentNote?.trim() || undefined,
    });
    return true;
  } catch (error) {
    console.error("[admin] mark payout paid failed", error);
    return false;
  }
}

export async function approveCreatorPayout(payoutId: string, reason: string, note?: string): Promise<boolean> {
  try {
    await axios.post(`/admin/finance/payouts/${payoutId}/approve`, { reason, note });
    return true;
  } catch (error) {
    console.error("[admin] approve payout failed", error);
    return false;
  }
}

export async function rejectCreatorPayout(payoutId: string, reason: string, note?: string): Promise<boolean> {
  try {
    await axios.post(`/admin/finance/payouts/${payoutId}/reject`, { reason, note });
    return true;
  } catch (error) {
    console.error("[admin] reject payout failed", error);
    return false;
  }
}
